import * as z from "zod";

const passwordValidation = z
  .string()
  .min(1, "Password is required")
  .min(8, "Password must be at least 8 characters long")
  .refine((value) => /[A-Z]/.test(value), "Include at least one capital letter")
  .refine((value) => /[a-z]/.test(value), "Include at least one small letter")
  .refine((value) => /[0-9]/.test(value), "Include at least one number")
  .refine(
    (value) => /[@$!%*#?&]/.test(value),
    "Include at least one special character (@$!%*#?&)"
  );

export const googleAnalyticsIdSchema = z.object({
  trackingId: z
    .string()
    .min(1, "Tracking ID is required")
    .refine(
      (value) => {
        // UA-XXXXX-Y or G-XXXXXXXX format
        const uaPattern = /^UA-\d{4,10}-\d{1,3}$/;
        const ga4Pattern = /^G-[A-Z0-9]{10,12}$/;
        return uaPattern.test(value) || ga4Pattern.test(value);
      },
      {
        message:
          "Invalid tracking ID format. Use UA-XXXXX-Y or G-XXXXXXXX format",
      }
    ),
});

export const metaPixelIdSchema = z.object({
  pixelId: z
    .string()
    .min(1, "Pixel ID is required")
    .regex(/^\d+$/, "Pixel ID must contain only numbers")
    .min(15, "Pixel ID must be at least 15 digits")
    .max(16, "Pixel ID cannot exceed 16 digits"),
});

export const mailchimpConfigSchema = z.object({
  apiKey: z
    .string()
    .min(1, "API Key is required")
    .refine(
      (value) => {
        // Mailchimp API key format: 32-character-key-datacenter (e.g., b1b9e51bd5cfbc55f66082320ece6913-us14)
        const pattern = /^[a-f0-9]{32}-[a-z]+[0-9]+$/i;
        return pattern.test(value);
      },
      {
        message: "Invalid API Key format. Should be like '32characterkey-datacenter' (e.g., abc123...def456-us14)",
      }
    ),
  listId: z
    .string()
    .min(1, "List ID is required")
    .min(8, "List ID must be at least 8 characters")
    .max(12, "List ID cannot exceed 12 characters")
    .regex(/^[a-f0-9]+$/i, "List ID must contain only letters and numbers"),
});

export const slackWebhookSchema = z.object({
  webhookUrl: z
    .string()
    .min(1, "Webhook URL is required")
    .url("Please enter a valid URL")
    .refine(
      (value) => {
        // Slack webhook URL format
        const pattern = /^https:\/\/hooks\.slack\.com\/services\//i;
        return pattern.test(value);
      },
      {
        message: "Invalid Slack webhook URL. Should start with 'https://hooks.slack.com/services/'",
      }
    ),
  channelName: z
    .string()
    .min(1, "Channel name is required")
    .refine(
      (value) => {
        // Channel should start with # or @ for DM
        const pattern = /^[#@].+$/;
        return pattern.test(value);
      },
      {
        message: "Channel name should start with # (for channels) or @ (for direct messages)",
      }
    ),
});

export const signUpSchema = z.object({
  email: z.string().min(1, "Email is required").email("Invalid email address"),
  password: passwordValidation,
});

export const signInSchema = z.object({
  email: z.string().min(1, "Email is required").email("Invalid email address"),
  password: z.string().min(1, "Password is required"),
});

export const forgotPasswordSchema = z.object({
  email: z.string().min(1, "Email is required").email("Invalid email address"),
});

export const resetPasswordSchema = z
  .object({
    password: passwordValidation,
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });
